/**
 * krokiVectorService.js — fdsvector ("sketch-app") kroki gömme servisi
 *
 * Kroki motoru artık AYRI BİR UYGULAMA: https://fdsvector.sakagrup.com
 * Bu dosya o uygulamanın `/embed/*` sayfalarını bizim sayfalarımıza iframe
 * olarak gömmek için gereken URL + oturum bilgisini üretir. Java gateway'e
 * giden bir çağrı YOKTUR — bu yüzden burada axios/USE_MOCK_DATA yok.
 *
 * ── OTURUM (önemli) ──────────────────────────────────────────────────────────
 * Gömülü uygulama Java gateway'in istediği `X-Token` / `X-User-Id` başlıklarını
 * ister; ancak bir iframe'e özel HTTP başlığı GEÇİRİLEMEZ. Gömme uygulaması bu
 * yüzden başlıkları postMessage ile alır:
 *
 *   1. iframe yüklenir, `POST /api/documents/hall-base` ile dokümanı çözmeye
 *      çalışır. Oturum yoksa 401 döner ve ekranda "Waiting for host session…"
 *      yazar.
 *   2. Biz (host) iframe'e şunu post ederiz:
 *        { type: "fdsvector:session", userId: <number>, token: "<string>" }
 *      ⚠ userId SAYI olmak zorunda — gömme tarafı `typeof userId !== "number"`
 *      ise mesajı sessizce yok sayar (bkz. getVectorSession()).
 *   3. Gömme uygulaması bunu `POST /api/fds/external/auth/exchange` ile kendi
 *      çerezine çevirir (`credentials: "include"`) ve dokümanı yeniden çözer.
 *
 * Gömme tarafı host'a `fdsvector:ready` post edebilir, ama bunu YALNIZCA kendi
 * derlemesinde `VITE_PARENT_ORIGIN` tanımlıysa yapar. 2026-08-25 tarihli canlı
 * derlemede bu değişken BOŞ — yani bize hiçbir mesaj gelmez ve "hazır" sinyali
 * bekleyemeyiz. Bu yüzden KrokiVectorEmbed oturumu iframe `load` olayında
 * gönderir ve birkaç kez tekrarlar. (Aynı boş liste, gömme tarafının gelen
 * mesajlarda origin kontrolü yapmadığı anlamına da gelir — localhost'tan da
 * çalışır.)
 *
 * Bilinen gömme uçları (İKİSİNİN İLK PARAMETRESİ FARKLI, dikkat):
 *   /embed/hall-floor-plan?fairgroundId=<fuar alanı>&hallId=<salon>[&hallName=]&mode=view|edit
 *       → salonun boş taban planı; dokümanı POST /api/documents/hall-base çözer
 *         (yoksa oluşturur), FDS verisi bağlı değildir.
 *   /embed/stand-plan?fairId=<fuar>&hallId=<salon>&mode=view|edit
 *       → salonun STAND yerleşimi; dokümanı GET /api/fds/fair-configs/hall-document
 *         çözer (fuar konfigürasyonundaki hallDocumentMap'ten, YOKSA oluşturmaz →
 *         eşleşmeyen salonda 404). Doküman açıldığında FDS verisi de çekilir:
 *         .../exhibitors, /assignments, /safety-checks, /personnel.
 *
 * ⚠ "stand-plan" TEK STANDI izole eden bir uç DEĞİL: URL'de standId yok, salonun
 * tamamını çizer. Stand bağı şekil düzeyindedir — `layer: "stands"` üzerindeki her
 * şeklin data'sında `standId`/`standNo`/`standCompanyShort` tutulur ve düzenleme
 * modunda sağdaki inspector panelinden bir FDS standına bağlanır.
 *
 * Ayrıntı + canlı doğrulama notları: util/knowledgebase/fdsvector-kroki-embed.md
 */
import { currentLanguage } from '../i18n';

// Gömme uygulamasının kökü. Yerelde farklı bir vector kurulumuna bakmak için
// .env'e REACT_APP_KROKI_VECTOR_URL koymak yeterli (bkz. util/env/.env.example).
export const KROKI_VECTOR_BASE_URL =
  process.env.REACT_APP_KROKI_VECTOR_URL || 'https://fdsvector.sakagrup.com';

// postMessage hedefi — "*" yerine daima bu origin kullanılır ki token yanlış
// bir pencereye düşmesin.
export const KROKI_VECTOR_ORIGIN = new URL(KROKI_VECTOR_BASE_URL).origin;

export const VECTOR_SESSION_MESSAGE = 'fdsvector:session';
export const VECTOR_READY_MESSAGE = 'fdsvector:ready';

/**
 * Gömme → host olayları (backlog #179, fdsvector `claude/fds-integration-c6f1a0d4`, 2026-09-26;
 * sketch-app/src/embed/EmbedCanvasHost.tsx + canvasProps.tsx). Her biri { type, ...payload }:
 *   loaded          { documentId, mode, standCount, activeLayerRefused }
 *                   activeLayerRefused (#219, fdsvector bd876b2): edit modunda istenen KORUMALI
 *                   katman (floorplan / infrastructure) bu kullanıcıya açılmadıysa o anahtar, yoksa null
 *   stand-click     { shapeId, standId, standNo }       — salt okunur modda bir standa tıklandı
 *   isolate-result  { mode, matched, missingStandIds }  — standIds= / selectedStandId= / focus sonucu
 *   saved           { documentId, changedStandIds }     — düzenleme modunda kaydedildi
 *   stand-linked    { shapeId, standId, standNo, saved:false } — şekle stand bağlandı (henüz kaydedilmedi)
 *   export-result   { requestId, format, dataUrl | error }
 * ⚠ fdsvector bunları YALNIZCA kendi derlemesinde VITE_PARENT_ORIGIN tanımlıysa gönderir; bugün
 * (2026-09-26) hiçbir fdsvector ortamında tanımlı değil (backlog #184) — o gelene kadar hiçbiri
 * ulaşmaz. KrokiVectorEmbed dinleyicileri hazır bekler; sayfalar olaysız da çalışmalı.
 */
export const VECTOR_EVENTS = {
  READY: 'fdsvector:ready',
  LOADED: 'fdsvector:loaded',
  STAND_CLICK: 'fdsvector:stand-click',
  ISOLATE_RESULT: 'fdsvector:isolate-result',
  SAVED: 'fdsvector:saved',
  STAND_LINKED: 'fdsvector:stand-linked',
  EXPORT_RESULT: 'fdsvector:export-result',
  // Backlog #656 (2026-11-01): every stand of the hall has a drawing - { total, hallId }.
  ALL_STANDS_DRAWN: 'fdsvector:all-stands-drawn',
};

/** Host → gömme komutları (backlog #181/#182). VITE_PARENT_ORIGIN yoksa fdsvector HEPSİNİ reddeder (#184). */
export const VECTOR_COMMANDS = {
  FOCUS: 'fdsvector:focus',
  SET_VIEW: 'fdsvector:set-view',
  COLOUR_STANDS: 'fdsvector:colour-stands',
  EXPORT: 'fdsvector:export',
};

/**
 * fdsvector katman ANAHTARLARI (görünen adlar #207'den beri: Salon Planı / Altyapı Planı / Alan
 * Planı). activeLayer ve layers= bu anahtarları bekler, görünen adı değil.
 */
export const KROKI_LAYERS = {
  FLOORPLAN: 'floorplan',           // Salon Planı (salon duvarları) — korumalı katman
  INFRASTRUCTURE: 'infrastructure', // Altyapı Planı — korumalı katman
  STANDS: 'stands',                 // Alan Planı (stand yerleşimi)
};

/** view = salt okunur, edit = kroki düzenlenebilir (gömme uygulamasının kendi araç çubuğu açılır). */
export const KROKI_MODES = {
  VIEW: 'view',
  EDIT: 'edit',
};

/** Gömme türleri — ilk parametrenin adı türe göre değişir (bkz. dosya başı). */
export const KROKI_EMBED_KINDS = {
  /** boş salon taban planı — ilk parametre fuar ALANI id'si */
  HALL_FLOOR_PLAN: 'hall-floor-plan',
  /** salonun stand yerleşimi + FDS verisi — ilk parametre FUAR id'si */
  STAND_PLAN: 'stand-plan',
};

/** Türe göre ilk sorgu parametresinin adı. */
export const KROKI_PRIMARY_PARAM = {
  [KROKI_EMBED_KINDS.HALL_FLOOR_PLAN]: 'fairgroundId',
  [KROKI_EMBED_KINDS.STAND_PLAN]: 'fairId',
};

/**
 * Salt okunur görünüm seçenekleri — fdsvector'ün `embed/viewOptionsFromUrl.ts`
 * sözleşmesi (2026-09-24, fdsvector `claude/embed-layers-isolate-c6f1a0d4`).
 * Hepsi isteğe bağlı; yalnızca mode=view'de anlamlı:
 *
 *   layers          'default' (salon katmanları + sağdaki katman paneli — paneli
 *                   gösteren TEK değer) | 'none' (aynı katmanlar, panel yok, soluklaştırma
 *                   yok) | ['stands', ...] (yalnızca bu katman anahtarları, panel yok)
 *   dimInactive     boolean
 *   forceLabels     boolean
 *   selectedStandId FDS stand id'si — tüm salon çizilir, yalnızca bu stand etiketli +
 *                   vurgulu (şeklin data.standId'si bu id olmalı)
 *   standIds        [id, ...] — YALNIZCA bu standlar çizilir, çerçeveye sığdırılır
 *   shapeIds        [id, ...] — aynısı, fdsvector'ün kendi şekil id'leriyle
 *   area            { x1, y1, x2, y2 } metre (cetveldeki sayılar) — yalnızca bu alan
 *   areaMatch       'intersects' (varsayılan) | 'inside'
 *   rect            { x, y, width, height } metre — area'nın sol-üst köşe + boyut hali (backlog #244)
 *   around          [standId, …] — bu FDS standlarının ÇEVRESİ: kutuları margins kadar büyütülür,
 *                   area gibi kırpılarak gösterilir (Stand Detay › Konum › Alan, #242)
 *   aroundShapeIds  [shapeId, …] — aynısı fdsvector şekil id'leriyle
 *   margins         { left, top, right, bottom } metre — around varsa alanı tanımlar, yoksa çerçeveyi
 *                   (standIds/area eşleşmesi ya da fit hedefi) büyütür (#244)
 *   fit             'hall' | 'content' — izole OLMAYAN görünümü açılışta salona (Salon Planı çerçevesi)
 *                   ya da tüm çizime sığdırır (Konum › Salon, #244)
 *   chrome          'full' | 'none'
 *   standColours    { [standId]: css renk } — yalnızca view (backlog #182). URL parametresi
 *                   olarak gider: VITE_PARENT_ORIGIN gerektirmez, ama küçük kümeler için
 *                   (fdsvector'ün kendi notu). Büyük kümeler fdsvector:colour-stands mesajıyla.
 *   lang            'tr' | 'en' — fdsvector arayüz dili. #339: verilmezse buildEmbedUrl'in `lang`
 *                   parametresi, o da yoksa FDS'in arayüz dili gider (fdsvector'ün kendi varsayılanı en).
 *   viewer          'organizer' — Company-Portal #322/#323: organizatörün yalnızca görüntüleme krokisi.
 *                   fdsvector modu view'e zorlar, organizatör panelini (arama, vurgu süzgeci, stand
 *                   etiketleri: no / katılımcı / kW / alan / tip) açar ve dışa aktarmayı PNG ile sınırlar.
 *
 *   activeLayer     KROKI_LAYERS anahtarı — MODE=VIEW'DE bile (fdsvector #652, 2026-11-01): o katman aktif (tam görünür,
 *                   sekmesi seçili), diğerleri pasif (soluk, tıklanamaz). Katman paneliyle birlikte anlamlı
 *                   (layers verilmez ya da 'default'); 'none' / katman listesi kendi anlamını korur. Plan boş katmanda
 *                   "Bu katmanda henüz çizim yok" gösterir. Alan Planı açılışı: { activeLayer: 'stands', fit: 'hall', forceLabels: true }.
 *   lockLayer       true → view'de sekmeler görünür ama katman değiştirilemez (activeLayer ile).
 *
 * Düzenleme moduna özel (buildEmbedUrl'in kendi parametresi, viewOptions DEĞİL):
 *   activeLayer     KROKI_LAYERS anahtarı — edit modunda bu katmanda aç (backlog #205;
 *                   view modunda fdsvector yok sayar). Belgede o katman yoksa yok sayılır.
 *
 * standIds / shapeIds / area / rect / around verildiğinde fdsvector modu kendisi view'e zorlar ve
 * başlık/araç çubuğu/panel/altbilgi göstermez.
 */
const VIEW_OPTION_KEYS = [
  'layers', 'dimInactive', 'forceLabels', 'selectedStandId',
  'standIds', 'shapeIds', 'area', 'areaMatch', 'chrome',
  // Backlog #182 / #179 (2026-09-26).
  'standColours', 'lang',
  // Backlog #244 (2026-09-27): read-only viewport from bounds / reference objects.
  'rect', 'around', 'aroundShapeIds', 'margins', 'fit',
  // Company-Portal #322 (2026-10-11): fdsVector #323's organizer viewer.
  'viewer',
  // Backlog #652 (2026-11-01): a view with an ACTIVE layer (the others passive, dimmed and inert), and the tabs locked. Honoured by
  // fdsvector only with the layer sidebar (layers omitted / 'default'): 'none' and a layers list keep their meaning and win.
  'activeLayer', 'lockLayer',
];

function viewOptionParam(key, value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(',') : null;
  if (key === 'area' && typeof value === 'object') {
    const { x1, y1, x2, y2 } = value;
    return [x1, y1, x2, y2].every(Number.isFinite) ? `${x1},${y1},${x2},${y2}` : null;
  }
  // Backlog #244: margins={left, top, right, bottom} → "l,t,r,b"; rect={x, y, width, height} → "x,y,w,h".
  if (key === 'margins' && typeof value === 'object') {
    const sides = [value.left, value.top, value.right, value.bottom].map(Number);
    return sides.every((n) => Number.isFinite(n) && n >= 0) ? sides.join(',') : null;
  }
  if (key === 'rect' && typeof value === 'object') {
    const parts = [value.x, value.y, value.width, value.height].map(Number);
    return parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0 ? parts.join(',') : null;
  }
  // standColours={101: '#e11d48', 102: 'green'} → "101:#e11d48,102:green" (URLSearchParams kodlar).
  if (key === 'standColours' && typeof value === 'object') {
    const pairs = Object.entries(value)
      .filter(([id, c]) => id !== '' && typeof c === 'string' && c)
      .map(([id, c]) => `${id}:${c}`);
    return pairs.length > 0 ? pairs.join(',') : null;
  }
  return String(value);
}

/**
 * Gömme adresi. `primaryId` türe göre fuar alanı (hall-floor-plan) ya da fuar
 * (stand-plan) id'sidir; `hallId` her iki türde de SALON id'sidir. `viewOptions`
 * için yukarıdaki listeye bak.
 */
export function buildEmbedUrl({
  kind = KROKI_EMBED_KINDS.HALL_FLOOR_PLAN,
  primaryId,
  hallId,
  hallName,
  mode = KROKI_MODES.VIEW,
  viewOptions,
  activeLayer,
  // Backlog #657 (2026-11-01): edit modunda bu FDS stand id'sini çizim için hazırla (fdsvector &standToDraw=; Krokiler'in "Çiz" düğmesi).
  standToDraw,
  lang,
}) {
  if (primaryId == null || hallId == null) return null;
  const primaryParam = KROKI_PRIMARY_PARAM[kind];
  if (!primaryParam) return null;

  const params = new URLSearchParams();
  params.set(primaryParam, String(primaryId));
  params.set('hallId', String(hallId));
  // hallName yalnızca hall-floor-plan'da anlamlı (doküman oluşturulurken ad olarak
  // kullanılıyor); stand-plan dokümanı zaten var olan bir kayda bağlanır.
  if (hallName && kind === KROKI_EMBED_KINDS.HALL_FLOOR_PLAN) params.set('hallName', String(hallName));
  params.set('mode', mode === KROKI_MODES.EDIT ? KROKI_MODES.EDIT : KROKI_MODES.VIEW);
  // Backlog #27: bir salonun taban planında yalnızca Altyapı (infrastructure) ve
  // Yerleşim Planı (floorplan) katmanları anlamlıdır — standlar, mobil nesneler ve
  // düzenleyicinin dahili background/shapes katmanları asla görünmemeli. fdsvector'ün
  // bu rota için kendi varsayılanı zaten aynı şeyi yapıyor (backlog #28), ama açık
  // parametre iki repo arasındaki sözleşmeyi görünür kılıyor — sessiz bir varsayılana
  // güvenmek yerine.
  if (kind === KROKI_EMBED_KINDS.HALL_FLOOR_PLAN) {
    params.set('layers', 'infrastructure,floorplan');
    params.set('dimInactive', 'false');
  }
  // Backlog #205: edit modunda hangi katmanda açılacağı (view'de fdsvector yok sayar — göndermiyoruz).
  if (mode === KROKI_MODES.EDIT && activeLayer) {
    params.set('activeLayer', String(activeLayer));
    // Backlog #228 (2026-09-27): FDS'ten açılan düzenleme katmana SABİT. lockLayer=1 → fdsvector
    // içinde katman değiştirme kapalı (#227); hall-floor-plan'da layers=<katman> → kenar çubuğunda
    // yalnızca o katman (#229). İkisini de fdsvector bugün yok sayar (edit modunda layers=
    // okunmuyor, lockLayer henüz yok) — #227 / #229 gelince bu adla uygular.
    params.set('lockLayer', '1');
    if (kind === KROKI_EMBED_KINDS.HALL_FLOOR_PLAN) params.set('layers', String(activeLayer));
  }
  if (mode === KROKI_MODES.EDIT && standToDraw != null && /^[A-Za-z0-9_-]{1,64}$/.test(String(standToDraw))) {
    params.set('standToDraw', String(standToDraw));
  }
  // Çağıranın açık görünüm seçenekleri yukarıdaki varsayılanları ezer.
  VIEW_OPTION_KEYS.forEach((key) => {
    const value = viewOptionParam(key, viewOptions?.[key]);
    if (value != null) params.set(key, value);
  });
  // Backlog #339: fdsvector'ün kendi TR/EN sözlüğü FDS'in dilini izlesin. fdsvector `lang`ı yüklenirken
  // okur (varsayılanı en): hiç gönderilmediğinde kroki herkese İngilizce açılıyordu. Her modda gider.
  if (!params.has('lang')) params.set('lang', lang === 'en' || lang === 'tr' ? lang : currentLanguage());
  return `${KROKI_VECTOR_BASE_URL}/embed/${kind}?${params.toString()}`;
}

/**
 * Bir fuar+salon çiftinin stand planı VAR MI — iframe'i mount etmeden önce sorar.
 *
 * Neden gerek var: iframe çapraz kaynak olduğu için içindeki hata ekranını
 * ("Failed to resolve stand plan (HTTP 404)") okuyamayız; kullanıcı sebebi
 * anlaşılmayan boş bir kutu görür. Bu uç CORS'a açık (localhost:3000'den
 * doğrulandı, 2026-08-25), yani aynı soruyu ana sayfadan sorup sonucu düzgün
 * anlatabiliyoruz.
 *
 * Gateway'e değil fdsvector'e gittiği için axios/getAuthHeader kullanmaz; oturum
 * bu ucun kendi çerezinden gelir (gömme akışındaki auth/exchange ile aynı çerez).
 *
 * @returns {Promise<{ok: boolean, documentId?: string, reason?: string, message?: string}>}
 *   reason: 'missing-id' | 'not-mapped' (404 — salon bu fuarda etkin değil) | 'error' | 'network'
 */
export async function resolveStandPlanDocument({ fairId, hallId }) {
  if (fairId == null || hallId == null) return { ok: false, reason: 'missing-id' };
  const url = `${KROKI_VECTOR_BASE_URL}/api/fds/fair-configs/hall-document`
    + `?fairId=${encodeURIComponent(fairId)}&hallId=${encodeURIComponent(hallId)}`;
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (res.ok) {
      const { documentId } = await res.json();
      return { ok: true, documentId };
    }
    const body = await res.json().catch(() => ({}));
    return {
      ok: false,
      reason: res.status === 404 ? 'not-mapped' : 'error',
      message: body.error || `HTTP ${res.status}`,
    };
  } catch (err) {
    return { ok: false, reason: 'network', message: String(err) };
  }
}

/**
 * Backlog #164: fdsvector'de SALON PLANI (hall-base dokümanı) olan her {fuar alanı, salon} çifti —
 * GET {vector}/api/fds/hall-floor-plan-map → [{ _id, fairgroundId, hallId, documentId }].
 *
 * ⚠ Bu uç fdsvector'de KİMLİK DOĞRULAMASIZ ve filtresiz: TÜM fuar alanlarının eşlemesini döner
 * (backlog #29'a not edildi). Okuma olduğu ve yalnızca id döndüğü için şimdilik kullanıyoruz;
 * filtreyi burada, fuarAlaniId'ye göre biz yapıyoruz. Veri büyürse filtreli bir uç gerekir.
 * "Doküman var" = plan açılmış demek, ÇİZİLMİŞ demek değil — 2026-08-25'te boş dokümanlar da
 * oluştu (bkz. KrokiVectorEmbed yan etki kapısı). Çizim ilerlemesi için #202'nin durumları var.
 * resolveStandPlanDocument ile aynı CORS yolu (credentials:'include', fdsvector CORS_ORIGINS).
 *
 * @returns {Promise<{ok: boolean, byHallId?: Map<string,string>, message?: string}>}
 *   byHallId: salonId (string) → documentId, yalnızca istenen fuar alanı için.
 */
export async function getHallFloorPlanDocuments(fuarAlaniId) {
  if (fuarAlaniId == null) return { ok: false, message: 'fuarAlaniId yok' };
  try {
    const res = await fetch(`${KROKI_VECTOR_BASE_URL}/api/fds/hall-floor-plan-map`, { credentials: 'include' });
    if (!res.ok) return { ok: false, message: `HTTP ${res.status}` };
    const rows = await res.json();
    const byHallId = new Map();
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      if (String(r.fairgroundId) === String(fuarAlaniId) && r.hallId != null && r.documentId) {
        byHallId.set(String(r.hallId), String(r.documentId));
      }
    });
    return { ok: true, byHallId };
  } catch (err) {
    return { ok: false, message: String(err) };
  }
}

/**
 * Gömme uygulamasına gönderilecek oturum. Java gateway ile AYNI localStorage
 * anahtarlarını okur (bkz. BackendConstants.getAuthHeader / MainContext login).
 * Oturum yoksa null döner — çağıran tarafın kullanıcıya "giriş yapın" demesi için.
 */
export function getVectorSession() {
  const token = localStorage.getItem('token');
  const userId = Number(localStorage.getItem('userId'));
  if (!token || !Number.isFinite(userId) || userId <= 0) return null;
  // userId sayı olmalı — gömme tarafı string userId'yi yok sayar.
  return { userId, token };
}
